import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCallback, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTickets } from 'contexts/TicketsContext';
import type { GetTicketsResponseDTO } from 'declarations/dto/tickets';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { RoutePathsE } from 'declarations/enums/routes';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { AssigneeFilterValuesE, TicketAssigneeE } from 'declarations/enums/tickets';
import type { NoopT } from 'declarations/types/common';
import { getValidationErrorMessage } from 'helpers/apiError';
import { useChangeTicketDetails, useUpdateTicketMessage } from 'hooks/api/useTickets';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import useChangeTicketDetailsForm from '../components/TicketDetailsPopup/hooks/useChangeTicketDetailsForm';
export interface Column {
  id: string;
  label: string;
  visible: boolean;
}
export interface TicketsPageHookProps {
  ticketsPages: GetTicketsResponseDTO[] | undefined | null;
}

/**
 * Map assignee display value to backend TicketUserTypes enum.
 * Backend enum: Undefined=0, Chirp=1 (Dealer), WarrCloud=2.
 * OEM is not a valid backend TicketUserTypes value.
 * When the assignee dropdown shows dealer options, the value can be a rooftop name or "Chirp".
 */
const toAssignToEnum = (assigneeName: string | undefined): string | null => {
  if (!assigneeName || assigneeName.trim() === '') return null;
  if (
    assigneeName === AssigneeFilterValuesE.WarrCloud ||
    assigneeName === TicketAssigneeE.WarrCloud
  )
    return 'WarrCloud';
  if (assigneeName === AssigneeFilterValuesE.OEM) return null;
  // Dealer, Chirp, or rooftop name (dealer) -> Chirp
  return 'Chirp';
};

export interface ChangeTicketsListFormValues {
  ticketId: string;
  ticketRoofTopId: string;
  amount: string;
  claimStatusId: number;
  claimSubmittedDate: Date | null;
  advisorNumber: string;
  techNumber: string;
  ticketPriorityId: number;
  subCategoryId: number | null;
  text: string;
  dmsScheduleAccountNumber: string | null;
  assigneeName: string;
}

export type SelectTicketPayloadT = {
  roNumber: string;
  lineNumber: string | null;
};

type UseTicketsPageT = {
  formMethods: UseFormReturn<Record<string, ChangeTicketsListFormValues>>;
  handleSaveChanges: (data: Record<string, ChangeTicketsListFormValues>) => Promise<void>;
  hasNextTicketsPage: boolean | undefined;
  handleSelectTicket: (ticketId: string, payload: SelectTicketPayloadT) => NoopT;
};

const useTicketsPage = ({ ticketsPages }: TicketsPageHookProps): UseTicketsPageT => {
  /**
   * Hooks
   */
  const dispatch = useAppDispatch();
  const {
    setTicketId,
    setEditMode,
    setOpenedTicketTab,
    setOpenedScheduleTicketTab,
    setActiveTab,
    saveTicketListScrollPosition,
    fetchTicketsParams,
  } = useTickets();
  const { activeTab } = useTickets();
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Handlers
   */

  const queryClient = useQueryClient();
  const formMethods = useForm<Record<string, ChangeTicketsListFormValues>>({ mode: 'onChange' });
  const { setSelectedTicketsIds, hasNextTicketsPage, selectedTicketsIds } = useTickets();
  const { ticketSubcategoriesOptions, ticketPrioritiesOptions } = useChangeTicketDetailsForm();
  const batchSaveRef = useRef<{ remaining: number; total: number } | null>(null);

  const changeTicketDetailsMutation = useChangeTicketDetails({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets, fetchTicketsParams]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsCountByRoofTop]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsTotalsByRoofTop]);
      const batch = batchSaveRef.current;
      if (batch && batch.remaining > 0) {
        batch.remaining -= 1;
        if (batch.remaining === 0) {
          batchSaveRef.current = null;
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: `${batch.total} ticket${
                batch.total === 1 ? '' : 's'
              } updated successfully!`,
              snackBarSeverity: 'success',
            })
          );
        }
      }
    },
    onError: error => {
      batchSaveRef.current = null;
      dispatch(
        updateSnackbarNotification({
          snackbarMessage: `Failed to update ticket details. ${getValidationErrorMessage(error)}`,
          snackBarSeverity: 'error',
        })
      );
    },
  });

  const updateTicketMessageMutation = useUpdateTicketMessage({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
    },
    onError: error => {
      dispatch(
        updateSnackbarNotification({
          snackbarMessage: `Failed to update ticket message. ${getValidationErrorMessage(error)}`,
          snackBarSeverity: 'error',
        })
      );
    },
  });

  const normalizeString = (str: string): string => str?.replace(/\s+/g, '').toLowerCase();
  const parseMessage = (value: string): string => {
    return value?.replace(/^.*?:\s*/, '');
  };

  const handleSaveChanges = useCallback(
    async (data: Record<string, ChangeTicketsListFormValues>) => {
      const mutationPromises: Promise<void>[] = [];
      let ticketsMutatedCount = 0;
      const findOptionIdByName = (
        options: { label: string; value: string | number }[],
        name: string | null
      ): string | number | null => {
        const normalizedTarget = name ? normalizeString(name) : null;
        const option = options.find(option => normalizeString(option.label) === normalizedTarget);
        return option ? option.value : null;
      };

      selectedTicketsIds.forEach(ticketId => {
        const ticketData = data[`[${ticketId}]`] ?? data[ticketId];
        const currentTicket = ticketsPages
          ?.flatMap(page => page.data)
          .find(ticket => ticket.id === ticketId);

        if (!currentTicket) {
          return;
        }

        const claimStatusId = ticketData?.claimStatusId ?? currentTicket.claimStatusId;
        const assigneeName = ticketData?.assigneeName ?? currentTicket.assigneeName;

        if (
          currentTicket.assigneeName === 'OEM' &&
          assigneeName !== currentTicket.assigneeName && // attempting reassignment
          (claimStatusId === 1 || claimStatusId === 7) //1-Pending, 7-ManualReview
        ) {
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: `Ticket ${currentTicket.roNumber} cannot be reassigned from OEM while claim status is ${currentTicket.claimStatus}.`,
              snackBarSeverity: 'warning',
            })
          );
          return; // skip mutation for this ticket
        }

        // Determine if this is a schedule ticket
        const isScheduleTicket = currentTicket.categoryName?.toLowerCase() === 'schedule';

        // Get subcategory ID - for schedule tickets use subCategoryId, for regular tickets use requestSubCategoryId
        const subCategoryId =
          ticketData?.subCategoryId ??
          (isScheduleTicket ? currentTicket.subCategoryId : currentTicket.requestSubCategoryId) ??
          findOptionIdByName(ticketSubcategoriesOptions, currentTicket.subCategory);

        const ticketPriorityId =
          ticketData?.ticketPriorityId ??
          ticketPrioritiesOptions.find(option => option.label === currentTicket.ticketPriority)
            ?.value;

        const payload = {
          ticketId,
          ticketRoofTopId: currentTicket.roofTop.id || '',
          amount: ticketData?.amount
            ? ticketData.amount.replace(/[^0-9.-]/g, '')
            : currentTicket.amount.toString(),
          claimStatusId,
          claimSubmittedDate: ticketData?.claimSubmittedDate
            ? format(ticketData.claimSubmittedDate, 'yyyy-MM-dd')
            : currentTicket.claimSubmittedDate,
          advisorNumber: ticketData?.advisorNumber || currentTicket.advisorNumber,
          techNumber:
            ticketData?.techNumber === '-'
              ? ''
              : ticketData?.techNumber || currentTicket.techNumber,
          // Schedule tickets only: subcategory supported; non-schedule reverted
          subCategoryId: isScheduleTicket && subCategoryId ? Number(subCategoryId) : null,
          requestSubCategoryId: null,
          dmsScheduleAccountNumber:
            ticketData?.dmsScheduleAccountNumber ?? currentTicket.dmsScheduleAccountNumber,
          ticketPriorityId,
          ...(toAssignToEnum(assigneeName) != null && {
            assignTo: toAssignToEnum(assigneeName),
          }),
        };

        ticketsMutatedCount += 1;
        mutationPromises.push(
          new Promise(resolve => {
            changeTicketDetailsMutation.mutate(payload, {
              onSettled: resolve,
            });
          })
        );
        const currentText = parseMessage(currentTicket.lastMessage?.text);
        if (ticketData?.text && ticketData.text !== currentText) {
          mutationPromises.push(
            new Promise(resolve => {
              updateTicketMessageMutation.mutate(
                { ticketId, text: ticketData.text, assignToOppositeUser: false },
                { onSettled: resolve }
              );
            })
          );
        }
      });

      if (ticketsMutatedCount > 0) {
        batchSaveRef.current = { remaining: ticketsMutatedCount, total: ticketsMutatedCount };
      }
      await Promise.allSettled(mutationPromises);
      setEditMode(false);
      setSelectedTicketsIds([]);
    },
    [
      changeTicketDetailsMutation,
      dispatch,
      setEditMode,
      setSelectedTicketsIds,
      updateTicketMessageMutation,
      ticketsPages,
      selectedTicketsIds,
      ticketPrioritiesOptions,
      ticketSubcategoriesOptions,
    ]
  );

  const handleSelectTicket = useCallback(
    (ticketId: string, payload: SelectTicketPayloadT) => (): void => {
      const tabLabel = payload.lineNumber
        ? `${payload.roNumber} - ${payload.lineNumber}`
        : payload.roNumber;
      const isFromScheduleList = activeTab === ActiveTabTypeE.Schedule;
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete('section');
      searchParams.delete('tabLabel');
      if (isFromScheduleList) {
        searchParams.set('section', 'schedule');
        searchParams.set('tabLabel', tabLabel);
      }
      const search = searchParams.toString() ? `?${searchParams.toString()}` : '';
      const path = `${RoutePathsE.TICKET_DETAIL}/${ticketId}${search}`;

      if (isFromScheduleList) {
        setOpenedScheduleTicketTab(tabLabel, ticketId);
        setActiveTab(ActiveTabTypeE.Schedule);
        sessionStorage.setItem('activeTab', ActiveTabTypeE.Schedule);
        // Preserve isScheduleTicketList - don't overwrite; ensures correct view (cards vs list) on return
      } else {
        setActiveTab(ActiveTabTypeE.Tickets);
        sessionStorage.setItem('activeTab', ActiveTabTypeE.Tickets);
        setOpenedTicketTab(tabLabel, ticketId);
      }
      saveTicketListScrollPosition();
      setTicketId(ticketId);
      navigate(path);
    },
    [
      activeTab,
      location.search,
      setOpenedTicketTab,
      setOpenedScheduleTicketTab,
      setActiveTab,
      setTicketId,
      navigate,
      saveTicketListScrollPosition,
    ]
  );

  return {
    handleSelectTicket,
    formMethods,
    handleSaveChanges,
    hasNextTicketsPage,
  };
};

export default useTicketsPage;
